import axios from 'axios';
const api = axios.create({
    baseURL: import.meta.env.VITE_API_URL ?? 'http://localhost:8000/api/v1',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    withCredentials: true,
});
api.interceptors.request.use((config) => {
    const raw = localStorage.getItem('earnpos-auth');
    const token = raw ? JSON.parse(raw)?.state?.token : null;
    if (token)
        config.headers.Authorization = `Bearer ${token}`;
    return config;
});
api.interceptors.response.use((res) => res, (err) => {
    if (err.response?.status === 401) {
        localStorage.removeItem('earnpos-auth');
        window.location.href = '/login';
    }
    return Promise.reject(err);
});
export default api;
// ── Auth ───────────────────────────────────────────────────────────────────
export const auth = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    logout: () => api.post('/auth/logout'),
    me: () => api.get('/auth/me'),
    register: (data) => api.post('/auth/register', data),
};
// ── Dashboard ──────────────────────────────────────────────────────────────
export const dashboard = {
    summary: (period = 'today', location_id) => api.get('/reports/sales/summary', { params: { period, location_id } }),
    byDay: (period = 'this_month') => api.get('/reports/sales/by-day', { params: { period } }),
    topProducts: (period = 'this_month') => api.get('/reports/products/top', { params: { period, limit: 5 } }),
    aiInsights: () => api.get('/ai/insights'),
    lowStock: (location_id) => api.get('/reports/inventory/low-stock', { params: { location_id } }),
    byMethod: (period = 'this_month') => api.get('/reports/sales/by-method', { params: { period } }),
};
// ── POS ────────────────────────────────────────────────────────────────────
export const pos = {
    products: (params) => api.get('/products', { params }),
    categories: () => api.get('/categories'),
    barcode: (code) => api.get(`/products/barcode/${code}`),
    searchCustomers: (q) => api.get('/customers', { params: { search: q, per_page: 8 } }),
    validateGiftCard: (code) => api.post(`/gift-cards/${code}/validate`),
    validateVoucher: (code, customer_id, subtotal_cents) => api.post('/vouchers/validate', { code, customer_id, subtotal_cents }),
    openSession: (data) => api.post('/cash-sessions/open', data),
    closeSession: (id, data) => api.post(`/cash-sessions/${id}/close`, data),
    activeSession: (register_id) => api.get('/cash-sessions/active', { params: { register_id } }),
    checkout: (cart) => api.post('/checkout', cart),
    syncOffline: (orders) => api.post('/orders/sync', { orders }),
    timeRules: () => api.get('/vouchers/time-rules'),
    registers: () => api.get('/registers'),
    locations: () => api.get('/locations'),
};
// ── Customers ──────────────────────────────────────────────────────────────
export const customers = {
    list: (params) => api.get('/customers', { params }),
    get: (id) => api.get(`/customers/${id}`),
    create: (data) => api.post('/customers', data),
    update: (id, data) => api.put(`/customers/${id}`, data),
    orders: (id) => api.get(`/customers/${id}/orders`),
    loyalty: (id) => api.get(`/customers/${id}/loyalty`),
    credit: (id) => api.get(`/customers/${id}/credit`),
    adjustPoints: (id, points, note) => api.post(`/customers/${id}/loyalty/adjust`, { points, note }),
    recordCreditPayment: (id, amount_cents, note) => api.post(`/customers/${id}/credit/payment`, { amount_cents, note }),
    export: () => api.get('/customers/export', { responseType: 'blob' }),
};
// ── Orders ─────────────────────────────────────────────────────────────────
export const ordersApi = {
    list: (params) => api.get('/orders', { params }),
    get: (id) => api.get(`/orders/${id}`),
    refund: (id, data) => api.post(`/orders/${id}/refund`, data),
    void: (id) => api.post(`/orders/${id}/void`),
    emailReceipt: (id, email) => api.post(`/receipts/${id}/email`, { email }),
    downloadReceipt: (id) => api.get(`/receipts/${id}`),
};
// ── Products ───────────────────────────────────────────────────────────────
export const productsApi = {
    list: (params) => api.get('/products', { params }),
    get: (id) => api.get(`/products/${id}`),
    create: (data) => api.post('/products', data),
    update: (id, data) => api.put(`/products/${id}`, data),
    delete: (id) => api.delete(`/products/${id}`),
    adjustStock: (id, data) => api.post(`/products/${id}/stock`, data),
    variants: (id) => api.get(`/products/${id}/variants`),
    variantOptions: (id) => api.get(`/products/${id}/variant-options`),
};
// ── Reports ────────────────────────────────────────────────────────────────
export const reports = {
    eod: (date) => api.get('/reports/eod', { params: { date } }),
    sales: (params) => api.get('/reports/sales', { params }),
    products: (params) => api.get('/reports/products', { params }),
    staff: (params) => api.get('/reports/staff', { params }),
    inventory: (location_id) => api.get('/reports/inventory', { params: { location_id } }),
    loyalty: (params) => api.get('/reports/loyalty', { params }),
    lowStock: (location_id) => api.get('/reports/inventory/low-stock', { params: { location_id } }),
    scheduled: {
        list: () => api.get('/scheduled-reports'),
        create: (data) => api.post('/scheduled-reports', data),
        update: (id, data) => api.put(`/scheduled-reports/${id}`, data),
        delete: (id) => api.delete(`/scheduled-reports/${id}`),
    },
};
// ── AI ─────────────────────────────────────────────────────────────────────
export const ai = {
    chat: (message, history) => api.post('/ai/chat', { message, history }),
    insights: () => api.get('/ai/insights'),
    fraudFlags: () => api.get('/ai/fraud-flags'),
    segments: () => api.get('/ai/segments'),
    pricingSuggestions: () => api.get('/ai/pricing-suggestions'),
    applyPricing: (id) => api.post(`/ai/pricing-suggestions/${id}/apply`),
    dismissPricing: (id) => api.post(`/ai/pricing-suggestions/${id}/dismiss`),
};
// ── Gift Cards ─────────────────────────────────────────────────────────────
export const giftCards = {
    list: (params) => api.get('/gift-cards', { params }),
    get: (code) => api.get(`/gift-cards/${code}`),
    issue: (data) => api.post('/gift-cards', data),
    reload: (code, amount_cents) => api.post(`/gift-cards/${code}/reload`, { amount_cents }),
    void: (id, reason) => api.post(`/gift-cards/${id}/void`, { reason }),
};
// ── Vouchers ───────────────────────────────────────────────────────────────
export const vouchersApi = {
    list: () => api.get('/vouchers'),
    create: (data) => api.post('/vouchers', data),
    update: (id, data) => api.put(`/vouchers/${id}`, data),
    delete: (id) => api.delete(`/vouchers/${id}`),
};
export const priceRules = {
    list: () => api.get('/price-rules'),
    create: (data) => api.post('/price-rules', data),
    update: (id, data) => api.put(`/price-rules/${id}`, data),
    delete: (id) => api.delete(`/price-rules/${id}`),
};
// ── Purchase Orders ────────────────────────────────────────────────────────
export const purchaseOrders = {
    list: (params) => api.get('/purchase-orders', { params }),
    get: (id) => api.get(`/purchase-orders/${id}`),
    create: (data) => api.post('/purchase-orders', data),
    markSent: (id) => api.post(`/purchase-orders/${id}/send`),
    receive: (id, data) => api.post(`/purchase-orders/${id}/receive`, data),
    autoReorder: (location_id) => api.post('/purchase-orders/auto-reorder', { location_id }),
    suppliers: () => api.get('/suppliers'),
};
// ── Roles ──────────────────────────────────────────────────────────────────
export const roles = {
    list: () => api.get('/roles'),
    create: (data) => api.post('/roles', data),
    update: (id, data) => api.put(`/roles/${id}`, data),
    delete: (id) => api.delete(`/roles/${id}`),
};
// ── Staff ──────────────────────────────────────────────────────────────────
export const staffApi = {
    list: () => api.get('/staff'),
    schedules: (params) => api.get('/staff/schedules', { params }),
    saveShift: (data) => api.post('/staff/schedules', data),
    deleteShift: (staff_id, date) => api.delete('/staff/schedules', { params: { staff_id, date } }),
    publishWeek: (week_start) => api.post('/staff/schedules/publish', { week_start }),
    copyWeek: (source, target) => api.post('/staff/schedules/copy', { source_week_start: source, target_week_start: target }),
    timesheets: (params) => api.get('/staff/timesheets', { params }),
    exportPayroll: (params) => api.get('/staff/timesheets/export', { params, responseType: 'blob' }),
};
// ── Settings ───────────────────────────────────────────────────────────────
export const settings = {
    locale: () => api.get('/locale/settings'),
    saveLocale: (data) => api.put('/locale/settings', data),
    loyalty: () => api.get('/loyalty/settings'),
    saveLoyalty: (data) => api.put('/loyalty/settings', data),
    features: () => api.get('/features'),
    saveFeatures: (data) => api.put('/features', data),
    receiptTemplate: () => api.get('/receipt-templates/default'),
    saveReceiptTemplate: (id, data) => id ? api.put(`/receipt-templates/${id}`, data) : api.post('/receipt-templates', data),
};
// ── Credit accounts ────────────────────────────────────────────────────────
export const creditAccounts = {
    summary: () => api.get('/credit-accounts/summary'),
    list: (params) => api.get('/credit-accounts', { params }),
    get: (id) => api.get(`/credit-accounts/${id}`),
    create: (data) => api.post('/credit-accounts', data),
    update: (id, data) => api.put(`/credit-accounts/${id}`, data),
    recordPayment: (id, data) => api.post(`/credit-accounts/${id}/payment`, data),
    adjust: (id, data) => api.post(`/credit-accounts/${id}/adjust`, data),
    balanceSheet: (as_of) => api.get('/credit-accounts/balance-sheet', { params: { as_of } }),
    statement: (id, from, to) => api.get(`/credit-accounts/${id}/statement`, { params: { from, to } }),
};
// ── Post-paid invoices ─────────────────────────────────────────────────────
export const postpaid = {
    summary: () => api.get('/postpaid/summary'),
    terms: () => api.get('/postpaid/terms'),
    list: (params) => api.get('/postpaid/invoices', { params }),
    get: (id) => api.get(`/postpaid/invoices/${id}`),
    recordPayment: (id, data) => api.post(`/postpaid/invoices/${id}/pay`, data),
    writeOff: (id, reason) => api.post(`/postpaid/invoices/${id}/write-off`, { reason }),
    sendInvoice: (id) => api.post(`/postpaid/invoices/${id}/send`),
};
// ── Referral programme ────────────────────────────────────────────────────────
export const referrals = {
    settings: () => api.get('/referrals/settings'),
    updateSettings: (data) => api.put('/referrals/settings', data),
    summary: () => api.get('/referrals/summary'),
    list: (p) => api.get('/referrals', { params: p }),
    getCode: (customerId) => api.get(`/referrals/code/${customerId}`),
    validate: (code, customerId, subtotal) => api.post('/referrals/validate', { code, customer_id: customerId, subtotal }),
    issueReward: (id) => api.post(`/referrals/${id}/issue`),
    void: (id) => api.post(`/referrals/${id}/void`),
};
